<template>
  <div
    data-test-id="nav-categories"
    class="flex items-center justify-center transition-[padding] scrollbar-hide"
    :class="{
      'lg:py-2 xl:py-5': !minified,
    }"
  >
    <Logo :size="minified ? 'sm' : 'md'" />
    <div
      v-if="!fetching"
      class="flex grow items-center self-stretch lg:justify-center"
    >
      <div
        class="flex items-center self-stretch lg:space-x-4 lg:px-4 xl:space-x-6 xl:px-6"
        role="menubar"
        :aria-label="$t('navigation.main_navigation_label')"
        @mouseleave="$emit('mouseleave:navigation', $event)"
      >
        <template v-for="item in navigationItems" :key="item.id">
          <AgsLink
            type="loud"
            :to="item?.path || ''"
            :class="{ 'text-red': item?.isSale }"
            class="navigation-item self-stretch px-2 font-bold normal-case lg:text-lg"
            data-qa="category"
            :data-qa-category="item?.name"
            data-test-id="main-navigation-category-link"
            role="menuitem"
            aria-haspopup="true"
            :aria-expanded="item.id === flyoutMenuCategoryId ? 'true' : 'false'"
            :aria-controls="`flyout-menu-${item.id}`"
            tabindex="0"
            @keydown="handleKeyboardNav($event, item)"
            @mouseenter="handleMouseEnter(item)"
            @click="handleItemClick($event, item)"
          >
            {{ item?.name }}
          </AgsLink>
        </template>
      </div>
    </div>
    <div v-if="!$device.isMobile" class="search">
      <LazyHeaderSearch />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Category } from '@scayle/storefront-nuxt'
import type { SbNavigation } from '~/cms/storyblok/types/storyblok.d.ts'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { useNavigationMapping } from '#layers/base/composables/useNavigationMapping'
import useUiState from '~/composables/ui/useUiState'
import type NavigationItem from '~/storyblok/navigation/NavigationItem.vue'

interface NavigationItem {
  id: string | number // Can be either Storyblok UUID or Scayle category ID
  name: string
  path: string
  isSale: boolean
  NavigationItem: any[]
  NavigationFlyoutContent: any[]
  ClickableImage: any[]
  children: any[]
}

type HeaderSubNavigationProps = {
  categories: Category[]
  blok: SbNavigation
  fetching: boolean
  minified?: boolean
  flyoutMenuCategoryId: number | string
}

const props = withDefaults(defineProps<HeaderSubNavigationProps>(), {
  minified: false,
})

const emit = defineEmits<{
  'click:item': [value: MouseEvent]
  'mouseleave:navigation': [value: MouseEvent | KeyboardEvent | NavigationItem]
  'mouseenter:item': [value: NavigationItem]
}>()

const { setLastInteractionType } = useUiState()
const { trackNavigationMain } = useTrackingEvents()
const { getNavigationItems } = useNavigationMapping()

const navigationItems = computed(() => {
  const activeStoryblokItems = props.blok?.MainNavigation || []
  return getNavigationItems(activeStoryblokItems, props.categories)
})

const handleItemClick = (event: MouseEvent, item: NavigationItem) => {
  trackNavigationMain(item.name)
  emit('click:item', event)
}

const handleMouseEnter = (item: NavigationItem) => {
  setLastInteractionType('mouse')
  emit('mouseenter:item', item)
}

const handleKeyboardNav = (event: KeyboardEvent, item: NavigationItem) => {
  setLastInteractionType('keyboard')

  const items = document.querySelectorAll(
    '[data-test-id="main-navigation-category-link"]',
  )
  const currentIndex = Array.from(items).findIndex((i) => i === event.target)

  switch (event.key) {
    case 'Enter':
    case 'ArrowDown':
      emit('mouseenter:item', item)
      break
    case 'Escape':
      emit('mouseleave:navigation', event)
      break
    case 'ArrowRight': {
      const next = items[currentIndex + 1] || items[0]
      ;(next as HTMLElement).focus()
      break
    }
    case 'ArrowLeft': {
      const prev = items[currentIndex - 1] || items[items.length - 1]
      ;(prev as HTMLElement).focus()
      break
    }
  }
}
</script>
